import type { KeyboardEvent } from "react";
import { ArrowDown2 } from "iconsax-reactjs";
import { Pencil, Plus, X } from "lucide-react";
import { formatPermissionLabel } from "../../../../shared/lib";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";

type InviteMembersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailInput: string;
  onEmailInputChange: (value: string) => void;
  onEmailKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onEmailBlur: () => void;
  emails: string[];
  onRemoveEmail: (email: string) => void;
  selectedRoles: string[];
  onRemoveSelectedRole: (role: string) => void;
  onOpenRolesModal: () => void;
  isRolesDisabled: boolean;
  selectedAdditionalPermissions: string[];
  onOpenPermissionsModal: () => void;
  onCancel: () => void;
  onInvite: () => void;
  canInvite: boolean;
  isInviting: boolean;
  inviteError: string | null;
};

const InviteMembersModal = ({
  open,
  onOpenChange,
  emailInput,
  onEmailInputChange,
  onEmailKeyDown,
  onEmailBlur,
  emails,
  onRemoveEmail,
  selectedRoles,
  onRemoveSelectedRole,
  onOpenRolesModal,
  isRolesDisabled,
  selectedAdditionalPermissions,
  onOpenPermissionsModal,
  onCancel,
  onInvite,
  canInvite,
  isInviting,
  inviteError,
}: InviteMembersModalProps) => {
  const permissionPills = selectedAdditionalPermissions.slice(0, 3);
  const remainingPermissionCount = Math.max(
    selectedAdditionalPermissions.length - 3,
    0,
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalHeader>
        <ModalTitle>Invite members</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
        >
          <X size={20} />
        </button>
      </ModalHeader>
      <ModalBody className="pt-4">
        <div className="space-y-6">
          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Email</div>
            <div className="mt-2 rounded-[14px] bg-[#EEF1FB] px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {emails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-2 rounded-full bg-[#E6E9F8] px-3 py-1 text-[14px] font-[450] text-[#2C2F44]"
                  >
                    {email}
                    <button
                      type="button"
                      className="text-[#2C2F44] hover:text-[#151827]"
                      onClick={() => onRemoveEmail(email)}
                      aria-label={`Remove ${email}`}
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
                <input
                  value={emailInput}
                  onChange={(event) => onEmailInputChange(event.target.value)}
                  onKeyDown={onEmailKeyDown}
                  onBlur={onEmailBlur}
                  placeholder={
                    emails.length === 0 ? "Use comma to separate emails" : ""
                  }
                  className="min-w-[180px] flex-1 bg-transparent text-[14px] text-[#2C2F44] outline-none placeholder:text-[#7A7F9A]"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">Role(s)</div>
            <div className="mt-2 flex items-center justify-between rounded-[14px] bg-[#EEF1FB] px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {selectedRoles.length === 0 ? (
                  <span className="text-[16px] text-[#7A7F9A]">
                    Select role(s)
                  </span>
                ) : (
                  selectedRoles.map((roleName) => (
                    <span
                      key={roleName}
                      className="inline-flex items-center gap-2 rounded-full bg-[#E6E9F8] px-3 py-1 text-[14px] font-[450] text-[#2C2F44]"
                    >
                      {roleName}
                      <button
                        type="button"
                        className="text-[#2C2F44] hover:text-[#151827]"
                        onClick={() => onRemoveSelectedRole(roleName)}
                        aria-label={`Remove ${roleName}`}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={onOpenRolesModal}
                className="flex items-center gap-2 text-[16px] font-[500] text-[#161827]"
                disabled={isRolesDisabled}
              >
                {selectedRoles.length === 0 ? "Select" : "Change"}
                <ArrowDown2 size={18} />
              </button>
            </div>
          </div>

          <div>
            <div className="text-[14px] font-[450] text-[#2C2F44]">
              Additional permissions
            </div>
            {selectedAdditionalPermissions.length === 0 ? (
              <Button
                variant="outline"
                className="mt-2 h-12 rounded-[14px] border-[#E1E6FF] text-[#2C2F44]"
                onClick={onOpenPermissionsModal}
              >
                <Plus size={16} />
                Add permissions
              </Button>
            ) : (
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {permissionPills.map((permission) => (
                    <span
                      key={permission}
                      className="rounded-full bg-[#EEF1FB] px-4 py-2 text-[14px] font-[350] text-gray-30"
                    >
                      {formatPermissionLabel(permission)}
                    </span>
                  ))}
                  {remainingPermissionCount > 0 ? (
                    <span className="rounded-full bg-[#EEF1FB] px-4 py-2 text-[14px] font-[350] text-gray-30">
                      +{remainingPermissionCount} more
                    </span>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  className="h-11 rounded-[14px] border-[#E1E6FF] text-[#2C2F44]"
                  onClick={onOpenPermissionsModal}
                >
                  <Pencil size={16} />
                  Edit permissions
                </Button>
              </div>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="ghost"
          className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className="h-11 rounded-[14px] px-6"
          onClick={onInvite}
          disabled={!canInvite || isInviting}
        >
          {isInviting ? "Inviting..." : "Invite"}
        </Button>
      </ModalFooter>
      {inviteError ? (
        <div className="px-6 pb-6 text-[14px] text-[#C24E4E]">
          {inviteError}
        </div>
      ) : null}
    </Modal>
  );
};

export default InviteMembersModal;
